import * as yup from "yup";
import { useTranslation } from "react-i18next";

export const appl41ValidationSchema = () => {
  const { t } = useTranslation();
  const minDate = new Date("1900-01-01");
  return yup
    .object()
    .shape({
      pastApp: yup
        .object()
        .shape({ m02_id: yup.string(), label: yup.string() })
        .nullable()
        .default(undefined)
        .transform((value, originalValue) => {
          if (
            originalValue &&
            typeof originalValue === "object" &&
            Object.keys(originalValue).length === 0
          ) {
            return undefined;
          }
          return value;
        }),
      d50_id: yup.object().required(t("Το πεδίο είναι υποχρεωτικό")),
      boardDate: yup
        .date()
        .required(t("Το πεδίο είναι υποχρεωτικό"))
        .min(minDate, t("Η ημερομηνία πρέπει να είναι μετά την 01/01/1900"))
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία")),
      acceptedFinancialReport: yup
        .string()
        .required(t("Το πεδίο είναι υποχρεωτικό")),
      fudocIsologismos: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
      fudocProsartima: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
      fudocEkthesiDS: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
      fudocWeb: yup.string().nullable(),
      webDate: yup
        .date()
        .nullable()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία")),
      webURL: yup.string().nullable(),
      paperDate: yup
        .date()
        .nullable()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία")),
      paperName: yup.string().nullable(),
      fudocPaper: yup.string().nullable(),
      usageFromDate: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .required(t("Το πεδίο είναι υποχρεωτικό"))
        .max(
          new Date("2016-07-07"),
          t("Η ημερομηνία πρέπει να είναι πριν από τις 07/07/2016"),
        )
        .test(
          "is-before",
          t(
            "Η ημερομηνία 'Ισολογισμού Χρήσης Από' πρέπει να είναι πριν από την ημερομηνία 'Ισολογισμού Χρήσης Έως'.",
          ),
          function (value) {
            const { usageToDate } = this.parent;
            if (
              value &&
              usageToDate &&
              new Date(value) > new Date(usageToDate)
            ) {
              return false;
            }
            return true;
          },
        ),
      usageToDate: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .required(t("Το πεδίο είναι υποχρεωτικό"))
        .max(
          new Date("2016-07-07"),
          t("Η ημερομηνία πρέπει να είναι πριν από τις 07/07/2016"),
        ),
    })
    .test(
      "has filled all of website",
      t(
        "Εάν επιθυμείτε να εισάγετε τα στοιχεία της ιστοσελίδας, πρέπει να συμπληρώσετε όλα τα αντίστοιχα πεδία.",
      ),
      function (value) {
        if (!value || typeof value !== "object") {
          return true;
        }

        const fudocWeb = value.fudocWeb?.trim() || null;
        const webDate = value.webDate ?? null;
        const webURL = value.webURL?.trim() || null;

        if (!fudocWeb && !webDate && !webURL) {
          return true;
        }

        const errors = [];
        if (!fudocWeb) {
          errors.push(
            this.createError({
              path: "fudocWeb",
              message: t(
                "Εάν συμπληρώσετε ένα πεδίο, πρέπει να συμπληρώσετε όλα.",
              ),
            }),
          );
        }
        if (!webDate) {
          errors.push(
            this.createError({
              path: "webDate",
              message: t(
                "Εάν συμπληρώσετε ένα πεδίο, πρέπει να συμπληρώσετε όλα.",
              ),
            }),
          );
        }
        if (!webURL) {
          errors.push(
            this.createError({
              path: "webURL",
              message: t(
                "Εάν συμπληρώσετε ένα πεδίο, πρέπει να συμπληρώσετε όλα.",
              ),
            }),
          );
        }

        return errors.length > 0 ? errors[0] : true;
      },
    )

    .test(
      "has filled all of paper",
      t(
        "Εάν επιθυμείτε να εισάγετε τα στοιχεία της εφημερίδας, πρέπει να συμπληρώσετε όλα τα αντίστοιχα πεδία.",
      ),
      function (value) {
        if (!value || typeof value !== "object") {
          return true;
        }

        const fudocPaper = value.fudocPaper?.trim() || null;
        const paperDate = value.paperDate ?? null;
        const paperName = value.paperName?.trim() || null;

        if (!fudocPaper && !paperDate && !paperName) {
          return true;
        }

        const errors = [];
        if (!fudocPaper) {
          errors.push(
            this.createError({
              path: "fudocPaper",
              message: t(
                "Εάν επιθυμείτε να εισάγετε τα στοιχεία της εφημερίδας, πρέπει να συμπληρώσετε όλα τα αντίστοιχα πεδία.",
              ),
            }),
          );
        }
        if (!paperDate) {
          errors.push(
            this.createError({
              path: "paperDate",
              message: t(
                "Εάν επιθυμείτε να εισάγετε τα στοιχεία της εφημερίδας, πρέπει να συμπληρώσετε όλα τα αντίστοιχα πεδία.",
              ),
            }),
          );
        }
        if (!paperName) {
          errors.push(
            this.createError({
              path: "paperName",
              message: t(
                "Εάν επιθυμείτε να εισάγετε τα στοιχεία της εφημερίδας, πρέπει να συμπληρώσετε όλα τα αντίστοιχα πεδία.",
              ),
            }),
          );
        }

        return errors.length > 0 ? errors[0] : true;
      },
    )
    .test(
      "has-filled-either-website-or-paper",
      t(
        "Πρέπει να συμπληρώσετε είτε τα στοιχεία της ιστοσελίδας είτε της εφημερίδας.",
      ),
      function (value) {
        if (!value || typeof value !== "object") {
          return true;
        }

        const fudocWeb = value.fudocWeb?.trim() || null;
        const webDate = value.webDate ?? null;
        const webURL = value.webURL?.trim() || null;

        const fudocPaper = value.fudocPaper?.trim() || null;
        const paperDate = value.paperDate ?? null;
        const paperName = value.paperName?.trim() || null;

        const hasWebsiteData = fudocWeb && webDate && webURL;
        const hasPaperData = fudocPaper && paperDate && paperName;

        if (hasWebsiteData || hasPaperData) {
          return true;
        }

        const errors = [];

        errors.push(
          this.createError({
            path: "fudocWeb",
            message: t(
              "Επιλέξτε είτε τα στοιχεία της εφημερίδας είτε της ιστοσελίδας.",
            ),
          }),
        );
        errors.push(
          this.createError({
            path: "webDate",
            message: t(
              "Επιλέξτε είτε τα στοιχεία της εφημερίδας είτε της ιστοσελίδας.",
            ),
          }),
        );
        errors.push(
          this.createError({
            path: "webURL",
            message: t(
              "Επιλέξτε είτε τα στοιχεία της εφημερίδας είτε της ιστοσελίδας.",
            ),
          }),
        );
        errors.push(
          this.createError({
            path: "fudocPaper",
            message: t(
              "Επιλέξτε είτε τα στοιχεία της εφημερίδας είτε της ιστοσελίδας.",
            ),
          }),
        );
        errors.push(
          this.createError({
            path: "paperDate",
            message: t(
              "Επιλέξτε είτε τα στοιχεία της εφημερίδας είτε της ιστοσελίδας.",
            ),
          }),
        );
        errors.push(
          this.createError({
            path: "paperName",
            message: t(
              "Επιλέξτε είτε τα στοιχεία της εφημερίδας είτε της ιστοσελίδας.",
            ),
          }),
        );

        return new yup.ValidationError(errors);
      },
    );
};
